import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { EnvelopeSimple, Link as LinkIcon, ClockClockwise } from "@phosphor-icons/react";

const STAGE_META = {
  upcoming: { label: "Upcoming", tone: "text-muted-foreground" },
  gentle: { label: "Gentle nudge", tone: "text-foreground" },
  first: { label: "1st reminder", tone: "text-accent-foreground" },
  second: { label: "2nd reminder", tone: "text-destructive" },
  final: { label: "Final notice", tone: "text-destructive" },
};

const TEMPLATES = {
  gentle: (inv, company, url) =>
    `Hi ${inv.counterparty},

A friendly reminder — invoice ${inv.invoice_number || inv.invoice_id.slice(0, 8)} for ${fmtMoney(inv.total, company.currency)} is due ${inv.due_date || "shortly"}.

Pay securely online: ${url}

Thank you,
${company.name}`,
  first: (inv, company, url) =>
    `Hi ${inv.counterparty},

Our records show invoice ${inv.invoice_number} for ${fmtMoney(inv.total, company.currency)} is now ${inv.days_overdue} day(s) past due. Please arrange payment at your earliest convenience.

Pay online: ${url}

If you've already paid, please disregard this message.

Regards,
${company.name}`,
  second: (inv, company, url) =>
    `Dear ${inv.counterparty},

Invoice ${inv.invoice_number} for ${fmtMoney(inv.total, company.currency)} is now ${inv.days_overdue} days overdue. This is our second reminder — please settle at your earliest opportunity.

Pay now: ${url}

If there is a problem with this invoice, please contact us immediately.

${company.name}`,
  final: (inv, company, url) =>
    `Dear ${inv.counterparty},

This is a final notice regarding invoice ${inv.invoice_number} for ${fmtMoney(inv.total, company.currency)}, now ${inv.days_overdue} days overdue.

Please pay within 7 days to avoid further action: ${url}

${company.name}`,
};

export default function RemindersPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [stageFilter, setStageFilter] = useState("all");
  const [scheduler, setScheduler] = useState(null);
  const [schedulerBusy, setSchedulerBusy] = useState(false);
  const [stagesDraft, setStagesDraft] = useState("");

  const load = async () => {
    if (!activeCompany) return;
    const [remRes, schedRes] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/reminders`),
      api.get(`/companies/${activeCompany.id}/reminder-scheduler`),
    ]);
    setRows(remRes.data);
    setScheduler(schedRes.data);
    setStagesDraft((schedRes.data.stages || [3, 7, 14, 30]).join(", "));
  };

  const saveScheduler = async (patch) => {
    if (!activeCompany) return;
    setSchedulerBusy(true);
    try {
      const { data } = await api.patch(
        `/companies/${activeCompany.id}/reminder-scheduler`,
        patch,
      );
      setScheduler(data);
      setStagesDraft((data.stages || []).join(", "));
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSchedulerBusy(false);
    }
  };

  const runNow = async () => {
    if (!activeCompany) return;
    setSchedulerBusy(true);
    try {
      const { data } = await api.post(`/companies/${activeCompany.id}/reminder-scheduler/run`);
      toast.success(`Scheduler ran — sent ${data.sent}, skipped ${data.skipped}, errors ${data.errors}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to run scheduler");
    } finally {
      setSchedulerBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const filtered = rows.filter((r) =>
    stageFilter === "all"
      ? r.days_overdue >= 0
      : stageFilter === "overdue"
      ? r.days_overdue > 0
      : r.suggested_stage === stageFilter
  );

  const sendReminder = async (row) => {
    const to = window.prompt(`Send reminder to (email):`, row.customer_email || "");
    if (!to) return;
    const stage = row.suggested_stage === "upcoming" ? "gentle" : row.suggested_stage;
    const url = row.pay_token ? `${window.location.origin}/pay/${row.pay_token}` : "";
    const tmpl = TEMPLATES[stage] || TEMPLATES.gentle;
    const message = tmpl(row, activeCompany, url);
    const subject = `${STAGE_META[stage]?.label || "Reminder"}: Invoice ${row.invoice_number || ""}`;
    try {
      const { data } = await api.post(`/companies/${activeCompany.id}/reminders/send`, {
        invoice_id: row.invoice_id,
        to,
        subject,
        message,
      });
      toast.success(`Reminder emailed to ${data.to}`);
      load();
    } catch (e2) {
      const detail = formatApiError(e2.response?.data?.detail) || e2.message;
      if (e2.response?.status === 503) {
        toast.error("Email not configured. Set RESEND_API_KEY.");
      } else {
        toast.error(detail);
      }
    }
  };

  const copyLink = async (row) => {
    if (!row.pay_token) return;
    await navigator.clipboard.writeText(`${window.location.origin}/pay/${row.pay_token}`);
    toast.success("Pay link copied");
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  const overdue = rows.filter((r) => r.days_overdue > 0);
  const totalOverdue = overdue.reduce((s, r) => s + r.total, 0);

  return (
    <>
      <PageHeader
        eyebrow="Cashflow"
        title="Payment Reminders"
        description="Every open AR invoice, prioritized by days overdue. Send a reminder that includes the pay link in one click."
        actions={
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger data-testid="reminder-filter" className="rounded-sm h-9 w-40 font-mono text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="font-mono">All open</SelectItem>
              <SelectItem value="overdue" className="font-mono">Overdue only</SelectItem>
              <SelectItem value="gentle" className="font-mono">Gentle</SelectItem>
              <SelectItem value="first" className="font-mono">1st reminder</SelectItem>
              <SelectItem value="second" className="font-mono">2nd reminder</SelectItem>
              <SelectItem value="final" className="font-mono">Final notice</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      <PageBody>
        {/* Auto-reminder scheduler */}
        {scheduler && (
          <div
            className="border border-border bg-card p-4 mb-4"
            data-testid="reminder-scheduler-card"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="overline flex items-center gap-2">
                  <ClockClockwise size={13} />
                  Auto-reminder scheduler
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  Automatically email overdue customers on an escalating cadence.
                  {scheduler.last_run_at && (
                    <span className="ml-1">
                      Last run: <span className="font-mono">{String(scheduler.last_run_at).slice(0, 16).replace('T',' ')}</span>
                      {scheduler.last_run_summary && (
                        <> · sent {scheduler.last_run_summary.sent}, skipped {scheduler.last_run_summary.skipped}, errors {scheduler.last_run_summary.errors}</>
                      )}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">
                    {scheduler.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <Switch
                    checked={!!scheduler.enabled}
                    disabled={schedulerBusy}
                    onCheckedChange={(v) => saveScheduler({ enabled: v })}
                    data-testid="reminder-scheduler-toggle"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={runNow}
                  disabled={schedulerBusy || !scheduler.enabled}
                  data-testid="reminder-scheduler-run-now"
                >
                  Run now
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Stages (days after due)
              </div>
              <Input
                value={stagesDraft}
                onChange={(e) => setStagesDraft(e.target.value)}
                placeholder="3, 7, 14, 30"
                className="rounded-sm font-mono text-sm w-52"
                data-testid="reminder-scheduler-stages"
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={schedulerBusy}
                onClick={() => {
                  const parsed = stagesDraft
                    .split(/[,\s]+/)
                    .map((v) => parseInt(v, 10))
                    .filter((v) => Number.isFinite(v) && v > 0);
                  if (parsed.length === 0) {
                    toast.error("Enter at least one positive number of days");
                    return;
                  }
                  saveScheduler({ stages: parsed });
                }}
                data-testid="reminder-scheduler-save-stages"
              >
                Save stages
              </Button>
              <span className="text-xs text-muted-foreground">
                Default: <span className="font-mono">3, 7, 14, 30</span>
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
          <div className="bg-card p-6">
            <div className="overline">Open AR Count</div>
            <div className="font-serif-display text-3xl num mt-2">{rows.length}</div>
          </div>
          <div className="bg-card p-6">
            <div className="overline">Overdue Count</div>
            <div className="font-serif-display text-3xl num mt-2 text-destructive" data-testid="overdue-count">
              {overdue.length}
            </div>
          </div>
          <div className="bg-card p-6">
            <div className="overline">Total Overdue</div>
            <div className="font-serif-display text-3xl num mt-2 text-destructive" data-testid="overdue-amount">
              {fmtMoney(totalOverdue, activeCompany.currency)}
            </div>
          </div>
        </div>

        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>#</Th>
                <Th>Customer</Th>
                <Th>Due</Th>
                <Th className="text-right">Days</Th>
                <Th>Stage</Th>
                <Th className="text-right">Amount</Th>
                <Th>Last Reminder</Th>
                <Th className="text-right pr-4">Action</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const stage = r.suggested_stage;
                const meta = STAGE_META[stage] || STAGE_META.gentle;
                return (
                  <tr
                    key={r.invoice_id}
                    data-testid={`reminder-row-${r.invoice_id}`}
                    className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200"
                  >
                    <Td className="num">{r.invoice_number || "—"}</Td>
                    <Td>{r.counterparty}</Td>
                    <Td className="num">{r.due_date || "—"}</Td>
                    <Td className={`text-right num ${r.days_overdue > 0 ? "text-destructive" : ""}`}>
                      {r.days_overdue > 0 ? `+${r.days_overdue}` : r.days_overdue < 0 ? r.days_overdue : "0"}
                    </Td>
                    <Td>
                      <span className={`tag-badge ${meta.tone}`}>{meta.label}</span>
                    </Td>
                    <Td className="text-right num font-medium">
                      {fmtMoney(r.total, activeCompany.currency)}
                    </Td>
                    <Td className="text-xs text-muted-foreground num">
                      {r.last_reminded_at ? (
                        <>
                          {r.last_reminded_at.slice(0, 10)}
                          <span className="ml-1 text-[10px]">×{r.reminder_count}</span>
                        </>
                      ) : (
                        "Never"
                      )}
                    </Td>
                    <Td className="text-right pr-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyLink(r)}
                          title="Copy pay link"
                          className="rounded-sm h-8"
                        >
                          <LinkIcon size={12} />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => sendReminder(r)}
                          data-testid={`send-reminder-${r.invoice_id}`}
                          className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                        >
                          <EnvelopeSimple size={12} className="mr-1" />
                          {r.reminder_count > 0 ? "Re-send" : "Remind"}
                        </Button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    <ClockClockwise size={20} className="inline mr-2 opacity-40" />
                    No invoices match this filter. All caught up.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
